import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Toggle } from "@its-engineering-and-development/ui-foundation/components";

import { useActivePolicyCategories, useAllPolicyCategories } from "@app/hooks/use-categories";
import { resolveExtraSelectedCategories } from "./category-selection";
import { Spinner } from "@app/components/base/spinner/spinner";
import { Drawer } from "@app/components/base/drawer/drawer";
import { SelectionCard } from "@app/components/base/selection-card/selection-card";
import { PARTICIPANT_LEGACY_IDS, MILEAGE_LEGACY_ID } from "@app/constants/expense-categories";
import type { GqlPolicy } from "@app/hooks/use-policy";

// ─── Types ────────────────────────────────────────────────────────────────────

type CategoryMode = "all" | "specific";
type AmountOperator = "greater_than" | "less_than" | "equal_to" | "gte" | "lte" | "between";
type ResponseType = "highlight" | "require_explanation";

type ChargeType = "internal" | "billable" | "any";

export interface CreateRuleFormState {
    ruleName: string;
    description: string;
    isActive: boolean;
    categoryMode: CategoryMode;
    selectedCategories: string[];
    expenseAmountEnabled: boolean;
    expenseAmountOperator: AmountOperator;
    expenseAmount: string;
    mealParticipantsEnabled: boolean;
    mealParticipantsOperator: AmountOperator;
    mealParticipantsCount: string;
    expenseAmountTo: string;
    chargeType: ChargeType;
    entryMethodEnabled: boolean;
    entryMethodSources: string[];
    receiptStatusEnabled: boolean;
    frequencyEnabled: boolean;
    responseType: ResponseType;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns true only when ALL selected categories support meal participants
 * (matched via legacyId → PARTICIPANT_LEGACY_IDS).
 * With "all" mode, show the toggle since it applies across all categories.
 * With "specific" mode and no selection, hide it until the user picks categories.
 */
function shouldShowMealParticipants(
    categoryMode: CategoryMode,
    selectedCategoryIds: string[],
    allCategories: { categoryId: string; legacyId: number | null }[],
): boolean {
    if (categoryMode === "all") return false;
    if (selectedCategoryIds.length === 0) return false;
    return selectedCategoryIds.every((id) => {
        const cat = allCategories.find((c) => c.categoryId === id);
        return cat?.legacyId != null && PARTICIPANT_LEGACY_IDS.has(cat.legacyId);
    });
}

const OPERATOR_LABELS: Record<AmountOperator, string> = {
    greater_than: "Greater than",
    less_than: "Less than",
    equal_to: "Equal to",
    gte: "Greater than or equal",
    lte: "Less than or equal",
    between: "Between",
};

const AMOUNT_OPERATORS: AmountOperator[] = ["greater_than", "less_than", "equal_to", "gte", "lte", "between"];

const RESPONSE_OPTIONS: {
    value: ResponseType;
    label: string;
    description: string;
    icon: () => React.ReactElement;
    enabled: boolean;
    accent: "amber" | "red";
}[] = [
    {
        value: "highlight",
        label: "Highlight violation",
        description: "Visual flag on expense line — no workflow impact",
        icon: FlagIcon,
        enabled: true,
        accent: "amber",
    },
    {
        value: "require_explanation",
        label: "Require explanation",
        description: "Submitter must provide justification before submitting",
        icon: PencilIcon,
        enabled: true,
        accent: "red",
    },
];

const DISABLED_RESPONSE_OPTIONS: { label: string; description: string; icon: () => React.ReactElement }[] = [
    {
        label: "Notify a role",
        description: "Alert a specific role without blocking submission",
        icon: BellIcon,
    },
    {
        label: "Require approval",
        description: "Add an approval step to the workflow",
        icon: CheckIcon,
    },
    {
        label: "Auto-block",
        description: "Prevent submission entirely",
        icon: BlockIcon,
    },
];

/** Generates a human-readable rule summary from current form state */
function buildRuleSummary(state: CreateRuleFormState, allCategories: { categoryId: string; categoryName: string }[]): string {
    const conditions: string[] = [];

    if (state.categoryMode === "specific" && state.selectedCategories.length > 0) {
        const names = state.selectedCategories.map((id) => allCategories.find((c) => c.categoryId === id)?.categoryName ?? id).join(", ");
        conditions.push(`Category is ${names}`);
    }

    if (state.expenseAmountEnabled && state.expenseAmount) {
        if (state.expenseAmountOperator === "between" && state.expenseAmountTo) {
            conditions.push(`Amount between $${state.expenseAmount} and $${state.expenseAmountTo}`);
        } else {
            const opLabel = OPERATOR_LABELS[state.expenseAmountOperator].toLowerCase();
            conditions.push(`Amount ${opLabel} $${state.expenseAmount}`);
        }
    }

    if (state.mealParticipantsEnabled && state.mealParticipantsCount) {
        const opLabel = OPERATOR_LABELS[state.mealParticipantsOperator].toLowerCase();
        conditions.push(`Meal participants ${opLabel} ${state.mealParticipantsCount} people`);
    }

    if (state.chargeType === "internal") {
        conditions.push("Charge type: Internal");
    } else if (state.chargeType === "billable") {
        conditions.push("Charge type: Client / billable");
    }

    if (state.entryMethodEnabled && state.entryMethodSources.length > 0) {
        conditions.push(`Entry method is ${state.entryMethodSources.join(" or ")}`);
    } else if (state.entryMethodEnabled) {
        conditions.push("Entry method / Source is enabled");
    }

    if (state.receiptStatusEnabled) {
        conditions.push("Receipt status is enabled");
    }

    if (state.frequencyEnabled) {
        conditions.push("Frequency / Velocity is enabled");
    }

    if (conditions.length === 0) return "";

    const responseLabel = state.responseType === "highlight" ? "Highlight violation" : "Require explanation (Free text)";
    return `IF ${conditions.join(" AND ")} THEN ${responseLabel}`;
}

// ─── CEL expression builder ──────────────────────────────────────────────────

const AMOUNT_OP_TO_SYMBOL: Record<AmountOperator, string> = {
    greater_than: ">",
    less_than: "<",
    equal_to: "==",
    gte: ">=",
    lte: "<=",
    between: "between",
};

const CEL_OP_TO_AMOUNT_OP: Record<string, AmountOperator> = {
    ">": "greater_than",
    ">=": "gte",
    "<": "less_than",
    "<=": "lte",
    "==": "equal_to",
};

export const ENTRY_SOURCE_MAP: Record<string, string> = {
    "Corporate card (matched)": "corporateCardMatched",
    "Manual / out-of-pocket": "manualOutOfPocket",
    "Travel booking feed": "travelBookingFeed",
};

const ENTRY_SOURCE_REVERSE: Record<string, string> = {
    corporateCardMatched: "Corporate card (matched)",
    manualOutOfPocket: "Manual / out-of-pocket",
    travelBookingFeed: "Travel booking feed",
};

/**
 * Builds a CEL expression string using the canonical field paths expected by
 * the backend: expense.amount, expense.mealParticipantCount, expense.entrySource,
 * expense.hasBillable.
 * Category scoping belongs in `rules.categoryIds`, not in the expression.
 */
export function buildCelExpression(state: CreateRuleFormState): string {
    const parts: string[] = [];

    if (state.expenseAmountEnabled && state.expenseAmount) {
        if (state.expenseAmountOperator === "between" && state.expenseAmountTo) {
            parts.push(`expense.amount >= ${parseFloat(state.expenseAmount)} && expense.amount <= ${parseFloat(state.expenseAmountTo)}`);
        } else if (state.expenseAmountOperator !== "between") {
            const sym = AMOUNT_OP_TO_SYMBOL[state.expenseAmountOperator];
            parts.push(`expense.amount ${sym} ${parseFloat(state.expenseAmount)}`);
        }
    }

    if (state.mealParticipantsEnabled && state.mealParticipantsCount) {
        const sym = AMOUNT_OP_TO_SYMBOL[state.mealParticipantsOperator];
        parts.push(`expense.mealParticipantCount ${sym} ${parseInt(state.mealParticipantsCount)}`);
    }

    if (state.entryMethodEnabled && state.entryMethodSources.length > 0) {
        const codes = state.entryMethodSources.map((s) => ENTRY_SOURCE_MAP[s]).filter(Boolean);
        if (codes.length === 1) {
            parts.push(`expense.entrySource == "${codes[0]}"`);
        } else {
            const orParts = codes.map((c) => `expense.entrySource == "${c}"`).join(" || ");
            parts.push(`(${orParts})`);
        }
    }

    if (state.chargeType === "internal") {
        parts.push("expense.hasBillable == false");
    } else if (state.chargeType === "billable") {
        parts.push("expense.hasBillable == true");
    }

    return parts.join(" && ");
}

// ─── Policy → Form state ──────────────────────────────────────────────────────

function parseCelIntoFormState(cel: string, state: CreateRuleFormState): void {
    // Between: expense.amount >= MIN && expense.amount <= MAX
    const betweenMatch = cel.match(/expense\.amount\s*>=\s*([\d.]+)\s*&&\s*expense\.amount\s*<=\s*([\d.]+)/);
    if (betweenMatch) {
        state.expenseAmountEnabled = true;
        state.expenseAmountOperator = "between";
        state.expenseAmount = String(parseFloat(betweenMatch[1]));
        state.expenseAmountTo = String(parseFloat(betweenMatch[2]));
    } else {
        const amountMatch = cel.match(/expense\.amount\s*(>=|<=|>|<|==)\s*([\d.]+)/);
        if (amountMatch) {
            state.expenseAmountEnabled = true;
            state.expenseAmountOperator = CEL_OP_TO_AMOUNT_OP[amountMatch[1]] ?? "greater_than";
            state.expenseAmount = String(parseFloat(amountMatch[2]));
        }
    }

    const mealMatch = cel.match(/expense\.mealParticipantCount\s*(>=|<=|>|<|==)\s*([\d]+)/);
    if (mealMatch) {
        state.mealParticipantsEnabled = true;
        state.mealParticipantsOperator = CEL_OP_TO_AMOUNT_OP[mealMatch[1]] ?? "gte";
        state.mealParticipantsCount = mealMatch[2];
    }

    // entry source: single or multiple occurrences of expense.entrySource == "..."
    const entrySourceMatches = [...cel.matchAll(/expense\.entrySource\s*==\s*"([^"]+)"/g)].map((m) => m[1]);
    if (entrySourceMatches.length > 0) {
        state.entryMethodEnabled = true;
        state.entryMethodSources = entrySourceMatches.map((s) => ENTRY_SOURCE_REVERSE[s] ?? s);
    }

    if (/expense\.hasBillable\s*==\s*false/.test(cel)) {
        state.chargeType = "internal";
    } else if (/expense\.hasBillable\s*==\s*true/.test(cel)) {
        state.chargeType = "billable";
    } else {
        state.chargeType = "any";
    }
}

const INITIAL_STATE: CreateRuleFormState = {
    ruleName: "",
    description: "",
    isActive: true,
    categoryMode: "all",
    selectedCategories: [],
    expenseAmountEnabled: true,
    expenseAmountOperator: "greater_than",
    expenseAmount: "",
    expenseAmountTo: "",
    mealParticipantsEnabled: false,
    mealParticipantsOperator: "greater_than",
    mealParticipantsCount: "",
    chargeType: "any",
    entryMethodEnabled: false,
    entryMethodSources: [],
    receiptStatusEnabled: false,
    frequencyEnabled: false,
    responseType: "highlight",
};

/**
 * Reconstructs a CreateRuleFormState from a persisted GqlPolicy.
 * Call this when opening the Update Rule drawer.
 */
export function policyToFormState(policy: GqlPolicy): CreateRuleFormState {
    const state: CreateRuleFormState = {
        ...INITIAL_STATE,
        ruleName: policy.name,
        description: policy.description ?? "",
        isActive: policy.status === "active",
        expenseAmountEnabled: false,
    };

    try {
        const rules = JSON.parse(policy.rules) as { categoryIds: string[]; expression: string };
        if (rules.categoryIds.length === 1 && rules.categoryIds[0] === "*") {
            state.categoryMode = "all";
            state.selectedCategories = [];
        } else {
            state.categoryMode = "specific";
            state.selectedCategories = rules.categoryIds;
        }
        parseCelIntoFormState(rules.expression, state);
    } catch {
        // fallback to defaults if AWSJSON parse fails
    }

    try {
        if (policy.actions.length > 0) {
            const firstAction = JSON.parse(policy.actions[0]) as { type: string };
            state.responseType = firstAction.type === "requireExplanation" ? "require_explanation" : "highlight";
        }
    } catch {
        // fallback to default
    }

    return state;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function FlagIcon() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7" />
        </svg>
    );
}

function PencilIcon() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path strokeLinecap="round" d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
    );
}

function BellIcon() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path strokeLinecap="round" d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M20 6 9 17l-5-5" />
        </svg>
    );
}

function BlockIcon() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="12" cy="12" r="10" />
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
        </svg>
    );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionCard({ children }: { children: React.ReactNode }) {
    return <div className="rounded-lg border border-gray-200 bg-white p-4">{children}</div>;
}

interface SegmentedControlProps<T extends string> {
    options: { value: T; label: string }[];
    value: T;
    onChange: (value: T) => void;
}

function SegmentedControl<T extends string>({ options, value, onChange }: SegmentedControlProps<T>) {
    return (
        <div className="inline-flex rounded-md border border-gray-200 bg-gray-50">
            {options.map((opt, i) => (
                <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    className={[
                        "px-4 py-1.5 text-sm font-medium transition-colors",
                        i === 0 ? "rounded-l-md" : "",
                        i === options.length - 1 ? "rounded-r-md" : "",
                        i !== 0 ? "border-l border-gray-200" : "",
                        value === opt.value ? "bg-white text-blue-600 shadow-sm ring-1 ring-inset ring-blue-200" : "text-gray-600 hover:bg-gray-100",
                    ].join(" ")}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const CATEGORY_MODE_OPTIONS: { value: CategoryMode; label: string }[] = [
    { value: "all", label: "All Categories" },
    { value: "specific", label: "Specific Categories" },
];

const EXTRA_CATEGORY_PILL_STYLES: Record<"active" | "inactive" | "deleted", string> = {
    active: "border-blue-500 bg-blue-50 text-blue-600",
    inactive: "border-orange-500 bg-orange-50 text-orange-600",
    deleted: "border-red-500 bg-red-50 text-red-600",
};

const ENTRY_METHOD_OPTIONS = ["Corporate card (matched)", "Manual / out-of-pocket", "Travel booking feed"];

export function CreateRuleDrawer({
    onClose,
    onSubmit,
    policyId,
    initialState,
    isLoading = false,
    canEdit = true,
}: {
    onClose: () => void;
    onSubmit: (state: CreateRuleFormState) => Promise<void> | void;
    policyId?: string;
    initialState?: CreateRuleFormState;
    isLoading?: boolean;
    canEdit?: boolean;
}) {
    const isUpdate = !!policyId;
    const [form, setForm] = useState<CreateRuleFormState>(initialState ?? INITIAL_STATE);
    const [validationErrors, setValidationErrors] = useState<{
        ruleName?: string;
        conditions?: string;
        expenseAmount?: string;
        mealParticipantsCount?: string;
        entryMethodSources?: string;
    }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { categories: activeCategories, isLoading: categoriesLoading } = useActivePolicyCategories();
    const { categoryMap, isLoading: allCategoriesLoading } = useAllPolicyCategories();
    const selectableCategories = useMemo(() => activeCategories.filter((c) => c.legacyId == null || c.legacyId !== MILEAGE_LEGACY_ID), [activeCategories]);

    // Selected categories with no pill in the active list (deactivated or deleted) so the match stays visible.
    const extraSelectedCategories = useMemo(
        () => resolveExtraSelectedCategories(form.selectedCategories, new Set(selectableCategories.map((c) => c.categoryId)), categoryMap),
        [form.selectedCategories, selectableCategories, categoryMap],
    );

    // Sync form when remote data arrives after the drawer is already open
    useEffect(() => {
        if (initialState) {
            setForm(initialState);
            setValidationErrors({});
        }
    }, [initialState]);

    const update = useCallback(<K extends keyof CreateRuleFormState>(key: K, value: CreateRuleFormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    }, []);

    const showMealParticipants = shouldShowMealParticipants(form.categoryMode, form.selectedCategories, selectableCategories);

    // Auto-clear meal participants when the toggle becomes hidden.
    // Guard against categories still loading — if we clear before `selectableCategories`
    // is populated, the condition is always false and will race with `initialState` sync.
    useEffect(() => {
        if (categoriesLoading) return;
        if (!showMealParticipants && form.mealParticipantsEnabled) {
            update("mealParticipantsEnabled", false);
        }
    }, [showMealParticipants, form.mealParticipantsEnabled, update, categoriesLoading]);

    const ruleSummary = useMemo(() => buildRuleSummary(form, selectableCategories), [form, selectableCategories]);
    const celExpression = useMemo(() => buildCelExpression(form), [form]);

    const validate = () => {
        const errors: { ruleName?: string; conditions?: string; expenseAmount?: string; mealParticipantsCount?: string; entryMethodSources?: string } = {};
        if (!form.ruleName.trim()) errors.ruleName = "Rule name is required";

        // At least one condition must be active and fully filled
        const amountActive = form.expenseAmountEnabled && !!form.expenseAmount.trim();
        const amountBetweenIncomplete = form.expenseAmountEnabled && form.expenseAmountOperator === "between" && !form.expenseAmountTo.trim();
        const participantsActive = form.mealParticipantsEnabled && !!form.mealParticipantsCount.trim();

        if (!amountActive && !participantsActive) {
            errors.conditions = "At least one condition (Expense Amount or Meal Participants) must be enabled and filled";
        }
        if (form.expenseAmountEnabled && !form.expenseAmount.trim()) {
            errors.expenseAmount = "Amount value is required";
        }
        if (amountBetweenIncomplete) {
            errors.expenseAmount = 'Both amount values are required for "Between"';
        }
        if (form.mealParticipantsEnabled && !form.mealParticipantsCount.trim()) {
            errors.mealParticipantsCount = "Participant count is required";
        }
        if (form.entryMethodEnabled && form.entryMethodSources.length === 0) {
            errors.entryMethodSources = "Select at least one entry method / source";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setIsSubmitting(true);
        try {
            await onSubmit(form);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Drawer
            title={!canEdit ? "Rule Details" : isUpdate ? "Update Rule" : "Create Rule"}
            onClose={onClose}
            maxWidth="560px"
            isLoading={isLoading}
            loadingLabel="Loading rule"
            loadingMessage="Loading rule details…"
            footer={
                canEdit ? (
                    <Button color="primary" size="md" onPress={handleSubmit} className="w-full justify-center" isDisabled={isLoading || isSubmitting}>
                        {isSubmitting ? (
                            <span className="flex items-center justify-center gap-2">
                                <Spinner className="h-4 w-4" />
                                {isUpdate ? "Updating…" : "Creating…"}
                            </span>
                        ) : isUpdate ? (
                            "Update Rule"
                        ) : (
                            "Create Rule"
                        )}
                    </Button>
                ) : undefined
            }
        >
            {!isLoading && (
                <>
                    {/* ── Rule Details ──────────────────────────────────────── */}
                    <section aria-labelledby="section-rule-details">
                        <div className="mb-3 flex items-center justify-between">
                            <h3 id="section-rule-details" className="text-base font-semibold text-gray-900">
                                Rule Details
                            </h3>
                            <div className="flex items-center gap-2">
                                <Toggle
                                    label={form.isActive ? "Active" : "Inactive"}
                                    isSelected={form.isActive}
                                    onChange={(val) => update("isActive", val)}
                                    size="md"
                                />
                            </div>
                        </div>

                        <SectionCard>
                            <div className="flex flex-col gap-3">
                                <div>
                                    <Input
                                        label="Rule name (e.g., 'High-value meal flag')"
                                        value={form.ruleName}
                                        onChange={(val) => {
                                            update("ruleName", val);
                                            if (validationErrors.ruleName) {
                                                setValidationErrors((prev) => {
                                                    const next = { ...prev };
                                                    delete next.ruleName;
                                                    return next;
                                                });
                                            }
                                        }}
                                        isInvalid={!!validationErrors.ruleName}
                                    />
                                    {validationErrors.ruleName && <p className="mt-1 text-sm font-medium text-red-600">{validationErrors.ruleName}</p>}
                                </div>
                                <Input label="Description (optional)" value={form.description} onChange={(val) => update("description", val)} />
                            </div>
                        </SectionCard>
                    </section>

                    {/* ── Conditions ────────────────────────────────────────── */}
                    <section aria-labelledby="section-conditions">
                        <div className="mb-1">
                            <h3 id="section-conditions" className="text-base font-semibold text-gray-900">
                                Conditions
                            </h3>
                            <p className="text-sm text-gray-500">All enabled conditions must be true (AND logic)</p>
                        </div>
                        {validationErrors.conditions && <p className="mb-2 text-xs font-medium text-red-600">{validationErrors.conditions}</p>}

                        <SectionCard>
                            <div className="flex flex-col gap-5">
                                {/* Expense Category */}
                                <div>
                                    <p className="mb-2 text-sm font-semibold text-gray-800">Expense Category</p>
                                    <SegmentedControl
                                        options={CATEGORY_MODE_OPTIONS}
                                        value={form.categoryMode}
                                        onChange={(val) => {
                                            update("categoryMode", val);
                                            if (val === "all") update("selectedCategories", []);
                                        }}
                                    />

                                    {form.categoryMode === "specific" && (
                                        <div className="mt-3">
                                            {categoriesLoading ? (
                                                <p className="text-sm text-gray-400">Loading categories…</p>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {selectableCategories.map((cat) => {
                                                        const selected = form.selectedCategories.includes(cat.categoryId);
                                                        return (
                                                            <button
                                                                key={cat.categoryId}
                                                                type="button"
                                                                onClick={() =>
                                                                    update(
                                                                        "selectedCategories",
                                                                        selected
                                                                            ? form.selectedCategories.filter((id) => id !== cat.categoryId)
                                                                            : [...form.selectedCategories, cat.categoryId],
                                                                    )
                                                                }
                                                                className={[
                                                                    "rounded-full border px-3 py-1 text-sm font-medium transition-colors",
                                                                    selected
                                                                        ? "border-blue-500 bg-blue-50 text-blue-600"
                                                                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
                                                                ].join(" ")}
                                                            >
                                                                {cat.categoryName}
                                                            </button>
                                                        );
                                                    })}
                                                    {!allCategoriesLoading &&
                                                        extraSelectedCategories.map((cat) => (
                                                            <button
                                                                key={cat.categoryId}
                                                                type="button"
                                                                onClick={() =>
                                                                    update(
                                                                        "selectedCategories",
                                                                        form.selectedCategories.filter((id) => id !== cat.categoryId),
                                                                    )
                                                                }
                                                                className={[
                                                                    "rounded-full border px-3 py-1 text-sm font-medium transition-colors",
                                                                    EXTRA_CATEGORY_PILL_STYLES[cat.variant],
                                                                ].join(" ")}
                                                            >
                                                                {cat.label}
                                                            </button>
                                                        ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Expense Amount */}
                                <div>
                                    <div className="flex items-center gap-2">
                                        <Toggle
                                            label="Expense Amount"
                                            isSelected={form.expenseAmountEnabled}
                                            onChange={(val) => {
                                                update("expenseAmountEnabled", val);
                                                if (validationErrors.expenseAmount || validationErrors.conditions) {
                                                    setValidationErrors((prev) => {
                                                        const n = { ...prev };
                                                        delete n.expenseAmount;
                                                        delete n.conditions;
                                                        return n;
                                                    });
                                                }
                                            }}
                                            size="md"
                                        />
                                    </div>
                                    {form.expenseAmountEnabled && (
                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                            <select
                                                value={form.expenseAmountOperator}
                                                onChange={(e) => {
                                                    update("expenseAmountOperator", e.target.value as AmountOperator);
                                                    if (e.target.value !== "between") update("expenseAmountTo", "");
                                                }}
                                                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            >
                                                {AMOUNT_OPERATORS.map((op) => (
                                                    <option key={op} value={op}>
                                                        {OPERATOR_LABELS[op]}
                                                    </option>
                                                ))}
                                            </select>
                                            <div
                                                className={[
                                                    "flex items-center rounded-md border bg-white shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500",
                                                    validationErrors.expenseAmount ? "border-red-400" : "border-gray-300",
                                                ].join(" ")}
                                            >
                                                <span className="border-r border-gray-300 px-2 py-2 text-sm text-gray-500">$</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    placeholder="0.00"
                                                    value={form.expenseAmount}
                                                    onChange={(e) => {
                                                        update("expenseAmount", e.target.value);
                                                        if (validationErrors.expenseAmount || validationErrors.conditions) {
                                                            setValidationErrors((prev) => {
                                                                const n = { ...prev };
                                                                delete n.expenseAmount;
                                                                delete n.conditions;
                                                                return n;
                                                            });
                                                        }
                                                    }}
                                                    className="w-28 rounded-r-md bg-transparent px-2 py-2 text-sm text-gray-700 focus:outline-none"
                                                />
                                            </div>
                                            {form.expenseAmountOperator === "between" && (
                                                <>
                                                    <span className="text-sm text-gray-500">and</span>
                                                    <div
                                                        className={[
                                                            "flex items-center rounded-md border bg-white shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500",
                                                            validationErrors.expenseAmount ? "border-red-400" : "border-gray-300",
                                                        ].join(" ")}
                                                    >
                                                        <span className="border-r border-gray-300 px-2 py-2 text-sm text-gray-500">$</span>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            placeholder="0.00"
                                                            value={form.expenseAmountTo}
                                                            onChange={(e) => {
                                                                update("expenseAmountTo", e.target.value);
                                                                if (validationErrors.expenseAmount) {
                                                                    setValidationErrors((prev) => {
                                                                        const n = { ...prev };
                                                                        delete n.expenseAmount;
                                                                        return n;
                                                                    });
                                                                }
                                                            }}
                                                            className="w-28 rounded-r-md bg-transparent px-2 py-2 text-sm text-gray-700 focus:outline-none"
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {validationErrors.expenseAmount && (
                                        <p className="mt-1 text-xs font-medium text-red-600">{validationErrors.expenseAmount}</p>
                                    )}
                                </div>

                                {/* Meal Participants — only shown when applicable */}
                                {showMealParticipants && (
                                    <div>
                                        <Toggle
                                            label="Meal Participants"
                                            isSelected={form.mealParticipantsEnabled}
                                            onChange={(val) => {
                                                update("mealParticipantsEnabled", val);
                                                if (validationErrors.mealParticipantsCount || validationErrors.conditions) {
                                                    setValidationErrors((prev) => {
                                                        const n = { ...prev };
                                                        delete n.mealParticipantsCount;
                                                        delete n.conditions;
                                                        return n;
                                                    });
                                                }
                                            }}
                                            size="md"
                                        />
                                        {form.mealParticipantsEnabled && (
                                            <div className="mt-2 flex items-center gap-2">
                                                <select
                                                    value={form.mealParticipantsOperator}
                                                    onChange={(e) => update("mealParticipantsOperator", e.target.value as AmountOperator)}
                                                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                >
                                                    {AMOUNT_OPERATORS.filter((op) => op !== "between").map((op) => (
                                                        <option key={op} value={op}>
                                                            {OPERATOR_LABELS[op]}
                                                        </option>
                                                    ))}
                                                </select>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    placeholder="0"
                                                    value={form.mealParticipantsCount}
                                                    onChange={(e) => {
                                                        update("mealParticipantsCount", e.target.value);
                                                        if (validationErrors.mealParticipantsCount || validationErrors.conditions) {
                                                            setValidationErrors((prev) => {
                                                                const n = { ...prev };
                                                                delete n.mealParticipantsCount;
                                                                delete n.conditions;
                                                                return n;
                                                            });
                                                        }
                                                    }}
                                                    className={[
                                                        "w-20 rounded-md border bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500",
                                                        validationErrors.mealParticipantsCount ? "border-red-400" : "border-gray-300",
                                                    ].join(" ")}
                                                />
                                                <span className="text-sm text-gray-500">people</span>
                                            </div>
                                        )}
                                        {validationErrors.mealParticipantsCount && (
                                            <p className="mt-1 text-xs font-medium text-red-600">{validationErrors.mealParticipantsCount}</p>
                                        )}
                                    </div>
                                )}

                                {/* Charge Type */}
                                <div>
                                    <p className="mb-2 text-sm font-semibold text-gray-800">Charge Type</p>
                                    <div className="flex flex-wrap gap-2">
                                        {(["internal", "billable", "any"] as ChargeType[]).map((opt) => {
                                            const labels: Record<ChargeType, string> = {
                                                internal: "Internal charge",
                                                billable: "Client / billable",
                                                any: "Any",
                                            };
                                            const selected = form.chargeType === opt;
                                            return (
                                                <button
                                                    key={opt}
                                                    type="button"
                                                    onClick={() => update("chargeType", opt)}
                                                    className={[
                                                        "rounded-full border px-3 py-1 text-sm font-medium transition-colors",
                                                        selected
                                                            ? "border-blue-500 bg-blue-50 text-blue-600"
                                                            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
                                                    ].join(" ")}
                                                >
                                                    {labels[opt]}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Entry Method / Source */}
                                <div>
                                    <Toggle
                                        label="Entry Method / Source"
                                        isSelected={form.entryMethodEnabled}
                                        onChange={(val) => {
                                            update("entryMethodEnabled", val);
                                            if (!val) {
                                                update("entryMethodSources", []);
                                                if (validationErrors.entryMethodSources) {
                                                    setValidationErrors((prev) => {
                                                        const n = { ...prev };
                                                        delete n.entryMethodSources;
                                                        return n;
                                                    });
                                                }
                                            }
                                        }}
                                        size="md"
                                    />
                                    {form.entryMethodEnabled && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {ENTRY_METHOD_OPTIONS.map((opt) => {
                                                const selected = form.entryMethodSources.includes(opt);
                                                return (
                                                    <button
                                                        key={opt}
                                                        type="button"
                                                        onClick={() => {
                                                            update(
                                                                "entryMethodSources",
                                                                selected ? form.entryMethodSources.filter((s) => s !== opt) : [...form.entryMethodSources, opt],
                                                            );
                                                            if (validationErrors.entryMethodSources) {
                                                                setValidationErrors((prev) => {
                                                                    const n = { ...prev };
                                                                    delete n.entryMethodSources;
                                                                    return n;
                                                                });
                                                            }
                                                        }}
                                                        className={[
                                                            "rounded-full border px-3 py-1 text-sm font-medium transition-colors",
                                                            selected
                                                                ? "border-blue-500 bg-blue-50 text-blue-600"
                                                                : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
                                                        ].join(" ")}
                                                    >
                                                        {opt}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {validationErrors.entryMethodSources && (
                                        <p className="mt-1 text-xs font-medium text-red-600">{validationErrors.entryMethodSources}</p>
                                    )}
                                </div>

                                {/* Receipt Status */}
                                <Toggle
                                    label="Receipt Status"
                                    isSelected={form.receiptStatusEnabled}
                                    onChange={(val) => update("receiptStatusEnabled", val)}
                                    size="md"
                                    isDisabled
                                />

                                {/* Frequency / Velocity */}
                                <Toggle
                                    label="Frequency / Velocity"
                                    isSelected={form.frequencyEnabled}
                                    onChange={(val) => update("frequencyEnabled", val)}
                                    size="md"
                                    isDisabled
                                />
                            </div>
                        </SectionCard>
                    </section>

                    {/* ── Response ──────────────────────────────────────────── */}
                    <section aria-labelledby="section-response">
                        <div className="mb-3">
                            <h3 id="section-response" className="text-base font-semibold text-gray-900">
                                Response
                            </h3>
                            <p className="text-sm text-gray-500">Choose an escalation level from least to most restrictive</p>
                        </div>

                        <div className="flex flex-col gap-2">
                            {RESPONSE_OPTIONS.map((opt) => (
                                <SelectionCard
                                    key={opt.value}
                                    icon={opt.icon}
                                    label={opt.label}
                                    description={opt.description}
                                    selected={form.responseType === opt.value}
                                    isDisabled={false}
                                    accent={opt.accent}
                                    showRadioIndicator
                                    onSelect={() => update("responseType", opt.value)}
                                />
                            ))}
                            {DISABLED_RESPONSE_OPTIONS.map((opt) => (
                                <SelectionCard
                                    key={opt.label}
                                    icon={opt.icon}
                                    label={opt.label}
                                    description={opt.description}
                                    selected={false}
                                    isDisabled={true}
                                    showRadioIndicator
                                />
                            ))}
                        </div>
                    </section>

                    {/* ── Preview ───────────────────────────────────────────── */}
                    <section aria-labelledby="section-preview">
                        <h3 id="section-preview" className="mb-3 text-base font-semibold text-gray-900">
                            Preview
                        </h3>

                        <SectionCard>
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Rule Summary</p>
                            <p className="text-sm text-gray-600">{ruleSummary || "Add conditions above to build your rule…"}</p>
                            {celExpression && celExpression !== "true" && (
                                <>
                                    <p className="mt-3 mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">CEL Expression</p>
                                    <pre className="overflow-x-auto rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700 ring-1 ring-gray-200">
                                        {celExpression}
                                    </pre>
                                </>
                            )}
                        </SectionCard>
                    </section>
                </>
            )}
        </Drawer>
    );
}
